import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft, ArrowRight, Link2, Check } from 'lucide-react';
import { api } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { NewsletterBanner } from '../components/NewsletterBanner';
import { RelatedPosts, type RelatedPost } from '../components/RelatedPosts';

type BlogPostData = {
  slug: string;
  title: string;
  excerpt: string;
  contentHtml: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  tags: string[];
  cta: string;
  publishedAt: string | null;
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

function setMetaTag(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/* ── Social icons ─────────────────────────────────────────── */

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.743l7.732-8.858L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const RedditIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
  </svg>
);

const ThreadsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.312-.883-2.371-.887h-.018c-.828 0-1.684.236-2.326.648-.672.43-1.23 1.118-1.527 1.97l-1.975-.636c.44-1.273 1.218-2.28 2.247-2.91.99-.608 2.168-.934 3.435-.956h.038c1.594.005 2.927.505 3.862 1.56 1.06 1.2 1.477 3.002 1.22 5.36.806.68 1.428 1.535 1.799 2.45.87 2.126.865 5.134-1.634 7.56-1.848 1.81-4.143 2.63-7.25 2.653zm.406-9.553c-.38 0-.77.023-1.163.073-.967.128-1.738.44-2.229.9-.41.382-.607.844-.576 1.376.046.838.544 1.47 1.401 1.786.615.228 1.356.279 2.007.128.988-.236 1.715-.745 2.163-1.513.424-.726.618-1.695.57-2.88a11.51 11.51 0 0 0-2.173-.87z" />
  </svg>
);

/* ── Share helpers ─────────────────────────────────────────── */

function shareUrl(network: string, url: string, title: string): string {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  switch (network) {
    case 'x':        return `https://x.com/intent/tweet?text=${t}&url=${u}`;
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case 'reddit':   return `https://www.reddit.com/submit?url=${u}&title=${t}`;
    case 'threads':  return `https://www.threads.net/intent/post?text=${t}%20${u}`;
    default: return '#';
  }
}

function openShare(network: string, url: string, title: string) {
  window.open(shareUrl(network, url, title), '_blank', 'noopener,noreferrer,width=600,height=480');
}

/* ── Share button (sidebar variant) ───────────────────────── */

type SideShareBtnProps = {
  label: string;
  color: string;
  onClick: () => void;
  children: React.ReactNode;
};

function SideShareBtn({ label, color, onClick, children }: SideShareBtnProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110 active:scale-95 ${color}`}
    >
      {children}
    </button>
  );
}

/* ── Share button (bottom variant) ────────────────────────── */

type BottomShareBtnProps = {
  label: string;
  color: string;
  hoverColor: string;
  onClick: () => void;
  children: React.ReactNode;
};

function BottomShareBtn({ label, color, hoverColor, onClick, children }: BottomShareBtnProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors ${color} ${hoverColor}`}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

/* ── Article body with mid-article newsletter banner ──────── */

/**
 * Split the article HTML in two roughly-equal halves at a top-level heading
 * (or paragraph) boundary, so a banner can be inserted mid-read. Returns
 * [firstHalf, secondHalf]; secondHalf is '' when the article is too short.
 */
function splitForBanner(html: string): [string, string] {
  for (const re of [/<\/h2>/gi, /<\/p>/gi]) {
    const marks = [...html.matchAll(re)];
    if (marks.length >= 3) {
      const m = marks[Math.floor(marks.length / 2)]!;
      const idx = m.index! + m[0].length;
      return [html.slice(0, idx), html.slice(idx)];
    }
  }
  return [html, ''];
}

/** Renders the article HTML with the newsletter banner woven in (mobile/tablet only). */
function ArticleBody({ html }: { html: string }) {
  const [first, second] = splitForBanner(html);
  return (
    <>
      <div className="blog-content mt-8" dangerouslySetInnerHTML={{ __html: first }} />
      {/* Mid-article capture — hidden on xl where the right rail shows it instead. */}
      <div className="xl:hidden my-9">
        <NewsletterBanner variant="inline" />
      </div>
      {second && <div className="blog-content" dangerouslySetInnerHTML={{ __html: second }} />}
    </>
  );
}

/* ── Main component ────────────────────────────────────────── */

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog', 'post', slug],
    queryFn: () =>
      api<{
        post: BlogPostData;
        jsonLd: Record<string, unknown>;
        related: RelatedPost[];
        productCta?: { network: string | null; path: string; label: string };
      }>(
        `/api/blog/${slug}`,
      ),
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    const { post, jsonLd } = data;
    document.title = post.metaTitle || post.title;
    setMetaTag('description', post.metaDescription || post.excerpt);

    let script = document.getElementById('blog-jsonld') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'blog-jsonld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      document.getElementById('blog-jsonld')?.remove();
    };
  }, [data]);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const title = data?.post.title ?? '';

  function copyLink() {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const NETWORKS = [
    { key: 'x',        label: 'X (Twitter)', color: 'bg-black',       Icon: XIcon },
    { key: 'facebook', label: 'Facebook',    color: 'bg-[#1877f2]',   Icon: FacebookIcon },
    { key: 'linkedin', label: 'LinkedIn',    color: 'bg-[#0a66c2]',   Icon: LinkedInIcon },
    { key: 'reddit',   label: 'Reddit',      color: 'bg-[#ff4500]',   Icon: RedditIcon },
    { key: 'threads',  label: 'Threads',     color: 'bg-black',       Icon: ThreadsIcon },
  ] as const;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      <main className="flex-1 w-full">
        {isLoading ? (
          <div className="py-32 flex items-center justify-center gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" /> Cargando artículo…
          </div>
        ) : isError || !data ? (
          <div className="py-32 flex flex-col items-center justify-center gap-3 text-slate-400">
            <p className="text-lg font-semibold text-slate-600">Artículo no encontrado</p>
            <Link to="/blog" className="text-brand-600 hover:text-brand-700 inline-flex items-center gap-1.5">
              <ArrowLeft size={15} /> Volver al blog
            </Link>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 py-10 flex gap-8 items-start justify-center">

            {/* ── Floating share sidebar (xl+) ── */}
            <aside className="hidden xl:flex flex-col items-center gap-2.5 sticky top-24 self-start shrink-0 w-10">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1 [writing-mode:vertical-lr] rotate-180">
                Compartir
              </span>
              {NETWORKS.map(({ key, label, color, Icon }) => (
                <SideShareBtn
                  key={key}
                  label={`Compartir en ${label}`}
                  color={color}
                  onClick={() => openShare(key, currentUrl, title)}
                >
                  <Icon />
                </SideShareBtn>
              ))}
              <button
                onClick={copyLink}
                title="Copiar enlace"
                aria-label="Copiar enlace"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 hover:bg-slate-300 transition-transform hover:scale-110 active:scale-95"
              >
                {copied ? <Check size={15} className="text-emerald-600" /> : <Link2 size={15} />}
              </button>
            </aside>

            {/* ── Article ── */}
            <article className="min-w-0 w-full max-w-3xl">
              <Link to="/blog" className="text-sm text-slate-500 hover:text-brand-600 inline-flex items-center gap-1.5">
                <ArrowLeft size={14} /> Blog
              </Link>

              <header className="mt-5">
                {data.post.category && (
                  <span className="text-xs font-semibold text-brand-600 uppercase tracking-wide">
                    {data.post.category}
                  </span>
                )}
                <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight tracking-tight">
                  {data.post.title}
                </h1>
                <p className="mt-3 text-sm text-slate-400">{fmtDate(data.post.publishedAt)}</p>
              </header>

              {data.post.featuredImageUrl && (
                <img
                  src={data.post.featuredImageUrl}
                  alt={data.post.featuredImageAlt}
                  className="mt-6 w-full rounded-2xl border border-slate-200"
                />
              )}

              <ArticleBody html={data.post.contentHtml} />

              {/* CTA — the trial, plus the product page of the network the
                  article is about (or the plans when it covers several). */}
              <div className="mt-12 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-white p-7 text-center">
                {/* Not post.cta: AI-written, and many promise a 7- or 14-day trial. */}
                <p className="text-lg font-semibold">
                  {data.productCta?.network
                    ? `Programa tus publicaciones de ${data.productCta.network} con Zettapost.`
                    : 'Programa todas tus redes sociales desde un solo lugar.'}
                </p>
                <p className="mt-1 text-sm text-white/80">Prueba 3 días gratis · cancela cuando quieras</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 bg-white text-brand-700 font-semibold rounded-xl px-5 py-2.5 hover:bg-slate-100 transition"
                  >
                    Probar Zettapost gratis <ArrowRight size={16} />
                  </Link>
                  {data.productCta && (
                    <Link
                      to={data.productCta.path}
                      className="inline-flex items-center gap-2 border border-white/50 text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-white/10 transition"
                    >
                      {data.productCta.label}
                    </Link>
                  )}
                </div>
              </div>

              {/* ── Bottom share block ── */}
              <div className="mt-10 pt-8 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-600 mb-4">
                  ¿Te ha gustado el artículo? Compártelo 🙌
                </p>
                <div className="flex flex-wrap items-center gap-2.5">
                  {NETWORKS.map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      onClick={() => openShare(key, currentUrl, title)}
                      title={`Compartir en ${label}`}
                      aria-label={`Compartir en ${label}`}
                      className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                    >
                      <Icon />
                      {label}
                    </button>
                  ))}
                  <button
                    onClick={copyLink}
                    aria-label="Copiar enlace"
                    className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                  >
                    {copied
                      ? <><Check size={14} className="text-emerald-600" /> <span className="text-emerald-600">¡Enlace copiado!</span></>
                      : <><Link2 size={14} /> Copiar enlace</>
                    }
                  </button>
                </div>
              </div>

              {/* ── Related posts slider ── */}
              <RelatedPosts posts={data.related} />
            </article>

            {/* ── Newsletter rail (xl+) ── */}
            <aside className="hidden xl:block w-72 shrink-0 sticky top-24 self-start">
              <NewsletterBanner variant="card" />
            </aside>

          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
